import Dialog from '@mui/material/Dialog';
import ThemeConstants from '../../../../config/theme/theme_constants';
import { BooleanQuestionStatisticsInterface, NumberQuestionStatisticsInterface, StringQuestionStatisticsInterface } from '../../domain/interfaces/question_stat_interface';
import { useGetSurveyStatistics } from '../../domain/hooks/get_survey_statistics';
import { SyncLoader } from 'react-spinners';
import { useTranslation } from '../../../../i18n/useTranslation';

interface RestartDialogProps {
    onClose: () => void;
    open: boolean;
    surveyId?: string;
}

const STARS_COUNT = 5;
const STAR_NUMBERS = Array.from({ length: STARS_COUNT }, (_, i) => i + 1);

const STAR_PATH = "M36.1951 15.5275C37.3925 11.8423 42.6061 11.8423 43.8035 15.5275L48.0818 28.6947C48.6173 30.3428 50.1531 31.4587 51.886 31.4587H65.7308C69.6057 31.4587 71.2168 36.4171 68.082 38.6947L56.8813 46.8325C55.4793 47.8511 54.8927 49.6565 55.4282 51.3046L59.7065 64.4718C60.9039 68.1571 56.686 71.2216 53.5511 68.944L42.3504 60.8062C40.9485 59.7876 39.0501 59.7876 37.6481 60.8062L26.4475 68.944C23.3126 71.2216 19.0947 68.1571 20.2921 64.4718L24.5704 51.3046C25.1059 49.6565 24.5192 47.8511 23.1173 46.8325L11.9166 38.6947C8.78177 36.4171 10.3929 31.4587 14.2678 31.4587H28.1126C29.8455 31.4587 31.3813 30.3428 31.9168 28.6947L36.1951 15.5275Z";

const SVG_STYLES = {
    filter: 'drop-shadow(0 0 4px rgba(0, 0, 0, 0.04)), drop-shadow(0 8px 16px rgba(0, 0, 0, 0.08))',
};

const AnswersDialog: React.FC<RestartDialogProps> = ({ onClose, open, surveyId }) => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    
    const { 
        data: statistics, 
        isLoading, 
        error, 
        refetch 
    } = useGetSurveyStatistics(surveyId, open);

    return (
        <Dialog
            fullWidth={true}
            onClose={onClose}
            open={open}
            slotProps={{
                backdrop: {
                    style: {
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    },
                },
                container: {
                    className: '!w-screen'
                },
                paper: {
                    className: '!rounded-[20px] !m-3 !w-full',
                },
            }}
        >
            <div className='flex flex-col items-center gap-6 w-full bg-white px-5 py-4.5'>
                <div className='flex flex-col w-full'>
                    <button
                        className="cursor-pointer p-1"
                        onClick={onClose}
                    >
                        <svg 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g>
                                <path d="M12 13.3998L7.1 18.2998C6.91667 18.4831 6.68334 18.5748 6.4 18.5748C6.11667 18.5748 5.88334 18.4831 5.7 18.2998C5.51667 18.1165 5.425 17.8831 5.425 17.5998C5.425 17.3165 5.51667 17.0831 5.7 16.8998L10.6 11.9998L5.7 7.0998C5.51667 6.91647 5.425 6.68314 5.425 6.3998C5.425 6.11647 5.51667 5.88314 5.7 5.6998C5.88334 5.51647 6.11667 5.4248 6.4 5.4248C6.68334 5.4248 6.91667 5.51647 7.1 5.6998L12 10.5998L16.9 5.6998C17.0833 5.51647 17.3167 5.4248 17.6 5.4248C17.8833 5.4248 18.1167 5.51647 18.3 5.6998C18.4833 5.88314 18.575 6.11647 18.575 6.3998C18.575 6.68314 18.4833 6.91647 18.3 7.0998L13.4 11.9998L18.3 16.8998C18.4833 17.0831 18.575 17.3165 18.575 17.5998C18.575 17.8831 18.4833 18.1165 18.3 18.2998C18.1167 18.4831 17.8833 18.5748 17.6 18.5748C17.3167 18.5748 17.0833 18.4831 16.9 18.2998L12 13.3998Z" fill="black"/>
                            </g>
                        </svg>
                    </button>
                    <h2 className='text-black text-xl font-medium leading-5 text-center w-full'>{t('survey.stats.title')}</h2>
                </div>

                <div className='flex flex-col items-center gap-5'>
                    {isLoading
                    ? <SyncLoader speedMultiplier={0.5} size={20} color="black" className='pb-6 pt-2'/>
                    : error
                        ? <div className="flex flex-col items-center gap-5 z-1">
                            <p className="font-semibold text-center text-sm">{t('survey.stats.fetch_error')}</p>
                            <button
                                className="rounded-full text-white font-bold px-8 py-2.5 min-h-10.5 min-w-51 cursor-pointer z-1"
                                style={{ backgroundColor: theme.UvibesPink }}
                                onClick={() => refetch()}
                            >
                                {t('common.button.retry')}
                            </button>
                        </div>
                        : statistics && statistics.map((stat) => {
                            if (stat.type === "boolean") {
                                const booleanStat = stat as BooleanQuestionStatisticsInterface;
                                return (
                                    <div key={stat.questionId} className='w-full flex flex-col gap-1.5'>
                                        <p className=' text-lg font-medium'>{stat.questionText}</p>
                                        <div className='flex flex-col gap-2'>
                                            <div className='flex items-center gap-2.5'>
                                                <div className='w-full max-w-25 rounded-full h-1.25' style={{ backgroundColor: `${theme.UvibesGreen}50` }}>
                                                    <div className={`h-full rounded-full`} style={{ backgroundColor: theme.UvibesGreen, width: `${booleanStat.truePercentage}%` }}/>
                                                </div>
                                                <p className='text-sm'>{t('survey.stats.yes', { percent: booleanStat.truePercentage, count: booleanStat.trueCount })}</p>
                                            </div>
                                            <div className='flex items-center gap-2.5'>
                                                <div className='w-full max-w-25 rounded-full h-1.25'  style={{ backgroundColor: `${theme.UvibesPink}50` }}>
                                                    <div className={`h-full rounded-full`} style={{ backgroundColor: theme.UvibesPink, width: `${booleanStat.falsePercentage}%` }}/>
                                                </div>
                                                <p className='text-sm'>{t('survey.stats.no', { percent: booleanStat.falsePercentage, count: booleanStat.falseCount })}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            } else if (stat.type === 'string') {
                                const stringStat = stat as StringQuestionStatisticsInterface;
                                return (
                                    <div key={stat.questionId} className='w-full flex flex-col gap-1.5'>
                                        <p className='text-lg font-medium'>{stat.questionText}</p>
                                        <p className='text-sm text-gray-600'>{t('survey.stats.responses', { count: stringStat.responseCount })}</p>
                                        {stringStat.sampleResponses.length > 0 && (
                                            <ul className='flex flex-col gap-1.5 mt-1 max-h-44 overflow-y-auto'>
                                                {stringStat.sampleResponses.map((response, index) => (
                                                    <li
                                                        key={`${stat.questionId}-${index}`}
                                                        className='text-sm px-3 py-2 rounded-lg bg-gray-100 break-words'
                                                    >
                                                        {response}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                );
                            } else if (stat.type === 'number') {
                                const numberStat = stat as NumberQuestionStatisticsInterface;
                                const averageRating = numberStat.average;

                                return (
                                    <div key={stat.questionId} className='w-full flex flex-col gap-1.5'>
                                        <p className=' text-lg font-medium'>{stat.questionText}</p>
                                        <div className='flex items-center gap-2'>
                                            <div className='flex items-center'>
                                                {STAR_NUMBERS.map((starNumber) => {
                                                    let fillPercentage = 0;
                                                    if (averageRating >= starNumber) {
                                                        fillPercentage = 100;
                                                    } else if (averageRating > starNumber - 1) {
                                                        fillPercentage = (averageRating - (starNumber - 1)) * 100;
                                                    }

                                                    return (
                                                        <div key={starNumber} className="relative">
                                                            <svg
                                                                viewBox="0 0 80 80"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                style={SVG_STYLES}
                                                                height={28}
                                                                width={28}
                                                            >
                                                                <path
                                                                    d={STAR_PATH}
                                                                    fill={'#E5E5E5'}
                                                                />
                                                            </svg>

                                                            {fillPercentage > 0 && (
                                                                <div 
                                                                    className="absolute top-0 left-0 overflow-hidden"
                                                                    style={{ width: `${fillPercentage}%` }}
                                                                >
                                                                    <svg
                                                                        viewBox="0 0 80 80"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        style={SVG_STYLES}
                                                                        height={28}
                                                                        width={28}
                                                                    >
                                                                        <path
                                                                            d={STAR_PATH}
                                                                            fill={theme.UvibesYellow}
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className='text-sm'>
                                                {t('survey.stats.rating', { average: averageRating.toFixed(1), count: numberStat.totalResponses })}
                                            </p>
                                        </div>
                                    </div>
                                );
                            }
                        })}
                    </div>
            </div>
        </Dialog>
    );
}

export default AnswersDialog;